import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { getLastIntrospectNodeByOperationAndParam } from '@adornis/introspect/api/get-introspect-node.js';
import { IntrospectViewer } from '@adornis/introspect/client/x-introspect-viewer.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { of } from 'rxjs';
import { setOdooConversationControlled, toggleIsOdooActive } from '../../api/clienthelper.js';
import { addCommentToOdooEntity } from '../../api/comments.js';
import { Comment } from '../../entities/comment.js';
import { OdooEntity } from '../../entities/odoo.js';

export const OdooTicketDetailView = stateful(
  ({ conversationID, onClose }: { conversationID: string | null; onClose: () => void }) => {
    const [newComment, setNewComment] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
    const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [showBodyHtml, setShowBodyHtml] = useState<boolean>(true);
    const [showMessagesBodyHtml, setShowMessagesBodyHtml] = useState<boolean>(true);
    const [showIntrospection, setShowIntrospection] = useState<boolean>(false);
    const [showPriorityIntrospection, setShowPriorityIntrospection] = useState<boolean>(false);
    const [showFilterIntrospection, setShowFilterIntrospection] = useState<boolean>(false);
    const [showTagIntrospection, setShowTagIntrospection] = useState<boolean>(false);

    const selectedConversation = useQuery(() => {
      if (!conversationID) return of(null);
      return getByID(
        OdooEntity,
        conversationID,
      )({
        _id: 1,
        subject: 1,
        body: 1,
        context: 1,
        conversationHistory: 1,
        odooModels: 1,
        odooTags: 1,
        team: 1,
        environment: 1,
        isPromptInjection: 1,
        answerScore: 1,
        answerComment: 1,
        guardrailPlainResponse: 1,
        priority: 1,
        createdAt: 1,
        comments: selectionSet(() => Comment, 1),
        odooTicketID: 1,
        attachments: { content: 1, contentID: 1, contentType: 1, fileName: 1, isInline: 1 },
        messages: {
          emailFrom: 1,
          body: 1,
          odooMailMessageId: 1,
          odooInsertedAt: 1,
          odooMessageType: 1,
          rating: 1,
          ratingText: 1,
        },
        answer: 1,
        isCheckedBySupport: 1,
        isOdooActive: 1,
      });
    }, [conversationID, refreshTrigger]);

    const introspectionNode = useQuery(() => {
      if (!conversationID) return of(null);
      return getLastIntrospectNodeByOperationAndParam(
        'generateAnswerForMail',
        JSON.stringify({ mailID: conversationID }),
      )({ _id: 1 });
    }, [conversationID]);

    const introspectionPriorityNode = useQuery(() => {
      if (!conversationID) return of(null);
      return getLastIntrospectNodeByOperationAndParam(
        'introspectPriority',
        JSON.stringify({ mailID: conversationID }),
      )({ _id: 1 });
    }, [conversationID]);

    const introspectionFilterNode = useQuery(() => {
      if (!conversationID) return of(null);
      return getLastIntrospectNodeByOperationAndParam(
        'generateFilterForMail',
        JSON.stringify({ mailID: conversationID }),
      )({ _id: 1 });
    }, [conversationID]);

    const introspectionTagNode = useQuery(() => {
      if (!conversationID) return of(null);
      return getLastIntrospectNodeByOperationAndParam(
        'introspectTag',
        JSON.stringify({ mailID: conversationID }),
      )({ _id: 1 });
    }, [conversationID]);

    const handleSetOdooConversationControlled = async () => {
      await setOdooConversationControlled(conversationID)();
    };

    const handleToggleOdooActive = async () => {
      if (!conversationID) return;
      await toggleIsOdooActive(conversationID)();
    };

    // Handle comment submission
    const handleSubmitComment = async () => {
      if (!newComment.trim() || !conversationID) return;

      try {
        setIsSubmitting(true);
        setToastMessage(null);

        await addCommentToOdooEntity(newComment.trim(), conversationID)({});
        setNewComment('');

        // Refresh the data by updating the refresh trigger
        setRefreshTrigger(refreshTrigger + 1);

        // Show success toast
        setToastMessage({ type: 'success', message: 'Comment added successfully!' });

        // Auto-dismiss toast after 3 seconds
        setTimeout(() => {
          setToastMessage(null);
        }, 3000);
      } catch (error) {
        console.error('Failed to add comment:', error);
        setToastMessage({ type: 'error', message: 'Failed to add comment' });
      } finally {
        setIsSubmitting(false);
      }
    };

    // Render comments
    const renderComments = (comments: Comment[]) => {
      if (!comments || comments.length === 0) {
        return html`<div class="text-sm italic text-base-content/50 p-4 text-center">No comments yet</div>`;
      }

      return html`
        <div class="space-y-3">
          ${comments.map(
            (comment, index) => html`
              <div class="bg-base-200 p-3 rounded-lg">
                <div class="flex justify-between items-start mb-2">
                  <div class="text-xs text-base-content/40">
                    ${comment.createdAt ? format(comment.createdAt, 'PPp') : 'Unknown date'}
                  </div>
                  <div class="text-xs text-base-content/50">#${index + 1}</div>
                </div>
                <div class="text-sm whitespace-pre-wrap">${comment.text}</div>
              </div>
            `,
          )}
        </div>
      `;
    };

    if (!conversationID) return nothing;

    // Show loading state if conversation data is not yet loaded
    if (selectedConversation.loading) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body flex items-center justify-center">
            <span class="loading loading-spinner loading-lg text-secondary"></span>
          </div>
        </div>
      `;
    }

    // Show error state if there was an error loading the conversation
    if (selectedConversation.error) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-xl font-bold">Conversation Details</h2>
              <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="flex flex-col items-center justify-center py-12 text-center">
              <div class="text-error mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <p class="text-error font-medium mb-2">Error loading conversation</p>
              <p class="text-base-content/70 text-sm">${selectedConversation.error.message}</p>
            </div>
          </div>
        </div>
      `;
    }

    // Show empty state if no conversation was found
    if (!selectedConversation.value) {
      return html`
        <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
          <div class="card-body">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-xl font-bold">Conversation Details</h2>
              <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  stroke="currentColor"
                  class="w-5 h-5"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div class="flex flex-col items-center justify-center py-12 text-center">
              <div class="text-base-content/50 mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-12 w-12"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 16l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <p class="font-medium mb-2">Conversation not found</p>
              <p class="text-base-content/70 text-sm">The requested conversation could not be found</p>
            </div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="card bg-base-100 shadow-xl border border-base-300 h-full">
        <div class="card-body">
          <div class="flex justify-between items-center">
            <h2 class="card-title text-xl font-bold">Ticket Details</h2>
            <button class="btn btn-sm btn-circle btn-ghost" @click=${onClose} title="Close">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="w-5 h-5"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p class="text-sm text-info">ID: ${selectedConversation.value._id}</p>
          <div class="divider"></div>

          <div class="space-y-4 overflow-y-auto max-h-[calc(100vh-300px)]">
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Odoo Ticket ID</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${selectedConversation.value.odooTicketID || 'N/A'}</div>
            </div>

            <! -- Handle set support checked-->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Already Checked</span>
                <span class="label-text-alt">Is checked by support</span>
              </label>
              <div class="bg-base-200 rounded-lg">
                <input
                  type="checkbox"
                  class="bg-base-200 text-sm resize-none"
                  .checked=${selectedConversation.value.isCheckedBySupport}
                  @click=${(e: Event) => handleSetOdooConversationControlled()}
                />
              </div>
            </div>

            <! -- Handle toggle ticket active in odoo -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Active ticket in Odoo</span>
                <span class="label-text-alt">Is active ticket in odoo - toggle to active/ deactivate</span>
              </label>
              <div class="bg-base-200 rounded-lg">
                <input
                  type="checkbox"
                  class="bg-base-200 text-sm resize-none"
                  .checked=${selectedConversation.value.isOdooActive != false}
                  @click=${(e: Event) => handleToggleOdooActive()}
                />
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Odoo Tags</span>
                <div class="flex space-x-2 ml-auto">
                  ${introspectionTagNode.value
                    ? html`
                        <button
                          class="btn btn-xs btn-outline"
                          @click=${() => setShowTagIntrospection(!showTagIntrospection)}
                        >
                          ${showTagIntrospection ? 'Hide Introspection Tag' : 'Show Introspection Tag'}
                        </button>
                      `
                    : nothing}
                </div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${selectedConversation.value.odooTags
                  ?.filter(obj => !obj.includes(':') && !obj.includes('my_data'))
                  .join(', ') || 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Priority</span>
                <div class="flex space-x-2 ml-auto">
                  ${introspectionPriorityNode.value
                    ? html`
                        <button
                          class="btn btn-xs btn-outline"
                          @click=${() => setShowPriorityIntrospection(!showPriorityIntrospection)}
                        >
                          ${showPriorityIntrospection ? 'Hide Introspection Priority' : 'Show Introspection Priority'}
                        </button>
                      `
                    : nothing}
                </div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${selectedConversation.value.priority || 'N/A'}</div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Created At</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${selectedConversation.value.createdAt ? format(selectedConversation.value.createdAt, 'PPpp') : 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">
                  ${selectedConversation.value.messages?.length
                    ? `Odoo Messages Thead Count: ${selectedConversation.value.messages.length}`
                    : 'No Messages in Odoo Thread after Description'}
                </span>

                ${selectedConversation.value.messages && selectedConversation.value.messages?.length > 0
                  ? html`<button
                      class="btn btn-xs btn-outline"
                      @click=${() => setShowMessagesBodyHtml(!showMessagesBodyHtml)}
                    >
                      ${showMessagesBodyHtml ? 'Show Plain' : 'Show HTML'}
                    </button> `
                  : ''}
              </label>
              <div class="space-y-2">
                ${selectedConversation.value.messages?.map(
                  (msg, idx) =>
                    html` <div>
                      <div
                        class="p-2 bg-base-200 rounded items-center gap-2"
                        style="
                          width: 85%;
                          float: ${msg.odooMessageType === 'CLIENT' ? 'left' : 'right'};
                          background-color: ${msg.odooMessageType === 'SOURCE'
                          ? '#DFFFE0'
                          : msg.odooMessageType === 'CLIENT'
                            ? '#FFF9D6'
                            : '#E0F7FF'};"
                      >
                        <!--<p class="text-sm font-medium"><strong>${`Message ${idx + 1}`}</strong></p>-->
                        <p class="text-sm font-medium">
                          <strong>${`Odoo Message ID ${msg.odooMailMessageId}`}</strong>
                        </p>
                        <p class="text-sm font-medium">
                          <strong>${`Timestamp ${msg.odooInsertedAt?.toISOString()}`}</strong>
                        </p>
                        <p class="text-sm font-medium"><strong>Sender:</strong>${msg.emailFrom || `N/A`}</p>
                        <p class="text-sm font-medium">
                          ${showMessagesBodyHtml
                            ? unsafeHTML(msg.body || '')
                            : html`<pre class="whitespace-pre-wrap"><strong>Content:</strong>${msg.body ||
                              'N/A'}</pre>`}
                        </p>
                      </div>
                    </div>`,
                )}
              </div>
            </div>

            <div class="form-control">
              <label class="label flex w-full justify-between">
                <span class="label-text font-medium">Priority</span>
                <div class="flex space-x-2 ml-auto">
                  ${introspectionPriorityNode.value
                    ? html`
                        <button
                          class="btn btn-xs btn-outline"
                          @click=${() => setShowPriorityIntrospection(!showPriorityIntrospection)}
                        >
                          ${showPriorityIntrospection ? 'Hide Introspection Priority' : 'Show Introspection Priority'}
                        </button>
                      `
                    : nothing}
                </div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">${selectedConversation.value.priority ?? 'N/A'}</div>
            </div>
            ${showPriorityIntrospection
              ? html`
                  <div class="modal modal-open">
                    <div class="modal-box w-full h-full p-0 relative max-w-full rounded-none">
                      ${IntrospectViewer({ nodeID: introspectionPriorityNode.value?._id })}
                      <button
                        class="btn btn-sm btn-circle absolute right-2 top-2"
                        @click=${() => setShowPriorityIntrospection(false)}
                        title="Close"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                `
              : nothing}

            <div class="form-control">
              <label class="label flex w-full justify-between">
                <span class="label-text font-medium">Subject</span>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
                ${selectedConversation.value.subject || 'N/A'}
              </div>
            </div>

            <div class="form-control">
              <label class="label flex w-full justify-between">
                <span class="label-text font-medium">Body</span>
                <button class="btn btn-xs btn-outline" @click=${() => setShowBodyHtml(!showBodyHtml)}>
                  ${showBodyHtml ? 'Show Plain' : 'Show HTML'}
                </button>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm">
                ${showBodyHtml
                  ? unsafeHTML(selectedConversation.value.body || '')
                  : html`<pre class="whitespace-pre-wrap">${selectedConversation.value.body || 'N/A'}</pre>`}
              </div>
            </div>

            <div class="form-control">
              <label class="label flex w-full justify-between">
                <span class="label-text font-medium">Answer</span>
                <div class="flex space-x-2 ml-auto">
                  ${introspectionNode.value
                    ? html`
                        <button class="btn btn-xs btn-outline" @click=${() => setShowIntrospection(!showIntrospection)}>
                          ${showIntrospection ? 'Hide Introspection' : 'Show Introspection'}
                        </button>
                      `
                    : nothing}
                </div>
              </label>
              <div class="bg-base-200 p-3 rounded-lg text-sm whitespace-pre-wrap">
                ${selectedConversation.value.answer || 'N/A'}
              </div>
            </div>
            ${showIntrospection
              ? html`
                  <div class="modal modal-open">
                    <div class="modal-box w-full h-full p-0 relative max-w-full rounded-none">
                      ${IntrospectViewer({ nodeID: introspectionNode.value?._id })}
                      <button
                        class="btn btn-sm btn-circle absolute right-2 top-2"
                        @click=${() => setShowIntrospection(false)}
                        title="Close"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                `
              : nothing}
            ${showFilterIntrospection
              ? html`
                  <div class="modal modal-open">
                    <div class="modal-box w-full h-full p-0 relative max-w-full rounded-none">
                      ${IntrospectViewer({ nodeID: introspectionFilterNode.value?._id })}
                      <button
                        class="btn btn-sm btn-circle absolute right-2 top-2"
                        @click=${() => setShowFilterIntrospection(false)}
                        title="Close"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                `
              : nothing}
            ${showTagIntrospection
              ? html`
                  <div class="modal modal-open">
                    <div class="modal-box w-full h-full p-0 relative max-w-full rounded-none">
                      ${IntrospectViewer({ nodeID: introspectionTagNode.value?._id })}
                      <button
                        class="btn btn-sm btn-circle absolute right-2 top-2"
                        @click=${() => setShowTagIntrospection(false)}
                        title="Close"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                `
              : nothing}
            ${showPriorityIntrospection
              ? html`
                  <div class="modal modal-open">
                    <div class="modal-box w-full h-full p-0 relative max-w-full rounded-none">
                      ${IntrospectViewer({ nodeID: introspectionPriorityNode.value?._id })}
                      <button
                        class="btn btn-sm btn-circle absolute right-2 top-2"
                        @click=${() => setShowPriorityIntrospection(false)}
                        title="Close"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                `
              : nothing}

            <div class="form-control">
              <label class="label flex w-full justify-between">
                <span class="label-text font-medium">Attachments</span>
              </label>
              <div class="space-y-2">
                ${selectedConversation.value.attachments?.map(
                  (att, idx) =>
                    html` <div class="p-2 bg-base-200 rounded flex items-center gap-2">
                      <span class="text-sm font-medium">${att.fileName || `Attachment ${idx + 1}`}</span>
                      ${att.contentType?.startsWith('image/')
                        ? html`<img src="data:${att.contentType};base64,${att.content}" class="max-h-32 rounded" />`
                        : html`<a
                            href="data:${att.contentType};base64,${att.content}"
                            download="${att.fileName}"
                            class="link text-sm"
                            target="_blank"
                            >Download</a
                          >`}
                    </div>`,
                )}
              </div>
            </div>

            <!-- Comments Section -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Comments</span>
                <span class="label-text-alt">${selectedConversation.value.comments?.length || 0} comments</span>
              </label>
              <div class="bg-base-200 rounded-lg">${renderComments(selectedConversation.value.comments)}</div>
            </div>

            <!-- Add Comment Form -->
            <div class="form-control">
              <label class="label">
                <span class="label-text font-medium">Add Comment</span>
              </label>
              <div class="relative">
                <textarea
                  class="textarea textarea-bordered w-full h-24 bg-base-200 text-sm resize-none"
                  placeholder="Add a comment..."
                  .value=${newComment}
                  @input=${(e: InputEvent) => setNewComment((e.target as HTMLTextAreaElement).value)}
                ></textarea>
                <div class="absolute bottom-3 right-3">
                  <button
                    class="btn btn-sm btn-primary"
                    @click=${handleSubmitComment}
                    ?disabled=${!newComment.trim() || isSubmitting}
                  >
                    ${isSubmitting
                      ? html`<span class="loading loading-spinner loading-xs mr-1"></span> Saving...`
                      : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Toast notifications -->
      ${toastMessage
        ? html`
            <div class="toast toast-end">
              <div class="alert ${toastMessage.type === 'success' ? 'alert-success' : 'alert-error'} shadow-lg">
                <div>
                  ${toastMessage.type === 'success'
                    ? html`
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      `
                    : html`
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-6 w-6"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      `}
                  <span>${toastMessage.message}</span>
                </div>
              </div>
            </div>
          `
        : ''}
    `;
  },
);
